// MEMBRESÍAS F1 (20-oct-2026). El negocio vende una membresía (meses y/o cupo de productos) y Lealify la reconoce:
// vender/renovar/cancelar desde la ficha, catálogo de planes y lista «por vencer». Los beneficios se configuran aquí
// y los aplica el motor en F2. Backend: lealify-base routes/loyalty.js (/membership-plans, /memberships,
// /contacts/:id/membership) + lib/memberships.js. Diseño: forobeta-ops/docs/lealify-membresias-plan-20261020.md
import { baseApi } from './baseClient';

export type BenefitRule =
  | { type: 'checkin_reward'; reward_id: number; per_day: 1 }
  | { type: 'quota_reward'; reward_ids: number[] }
  | { type: 'points_multiplier'; factor: number }
  | { type: 'cashback_pct'; pct: number }
  | { type: 'member_price'; pct_off: number };

export interface MembershipPlan {
  id: number;
  name: string;
  description: string | null;
  price: number | null;
  duration_months: number | null;   // 1..12; null = solo por productos incluidos
  quota_total: number | null;       // productos incluidos; null = sin productos incluidos
  leftover_months: number | null;   // solo planes por productos: meses tras los que caduca lo que sobre; null = no caduca (por defecto)
  accrues_points: boolean;
  benefits: BenefitRule[];
  notify: { enabled: boolean; channel: 'email' | 'whatsapp' | 'both' };
  status: 'active' | 'inactive' | 'archived';
  sort: number;
  members_active?: number;
  created_at: string;
  updated_at: string;
}
export interface PlanInput {
  name: string;
  description?: string | null;
  price?: number | null;
  duration_months?: number | null;
  quota_total?: number | null;
  accrues_points?: boolean;
  benefits?: BenefitRule[];
  notify?: { enabled: boolean; channel: 'email' | 'whatsapp' | 'both' };
  leftover_months?: number | null;   // solo planes por productos: null = lo que sobre no caduca
  status?: 'active' | 'inactive';
}

export type MembershipStatus = 'active' | 'expired' | 'exhausted' | 'cancelled' | 'replaced';
export interface ContactMembership {
  id: number;
  contact_id: number;
  plan_id: number;
  plan_name: string;
  starts_on: string;      // YYYY-MM-DD
  expires_on: string | null;   // YYYY-MM-DD inclusivo (día local del negocio); null = no caduca
  days_left: number | null;
  quota_total: number | null;
  quota_left: number | null;
  used: number | null;
  status: MembershipStatus;
  source: 'manual' | 'pos_sale' | 'migration';
  price_paid: number | null;
  payment_note: string | null;
  renewals: number;
  last_renewed_at: string | null;
  created_at: string;
  created_by_name: string | null;
  closed_at: string | null;
  close_reason: string | null;
  first_name?: string;
  last_name?: string | null;
  phone?: string | null;
  email?: string | null;
}
export interface MembershipSummary {
  plan_name: string; expires_on: string | null; days_left: number | null; quota_total: number | null; quota_left: number | null; message: string;
  /** F2 (20-oct-2026): recompensas que salen del CUPO (any=true → cualquiera) mientras quede cupo; null si el plan no tiene cupo de premios. */
  covers?: { any: boolean; ids: number[] } | null;
  no_points?: boolean;
}
/** ¿Esta recompensa sale del cupo de la membresía (y queda cupo)? */
export function membershipCovers(m: MembershipSummary | null | undefined, rewardId: number | string): boolean {
  if (!m || !m.covers || !(Number(m.quota_left) > 0)) return false;
  return m.covers.any || m.covers.ids.includes(Number(rewardId));
}
export interface ContactMembershipFicha { current: ContactMembership | null; history: ContactMembership[]; summary: MembershipSummary | null }
export interface MembershipCounts { active: number; expiring: number; expired: number }
export type MembershipFilter = 'active' | 'expiring' | 'expired' | 'all';
export interface MembershipUsageRow {
  id: number; kind: 'sale' | 'renewal' | 'cancel' | 'checkin_reward' | 'quota_reward' | 'adjust';
  delta: number | null; ref_type: string | null; ref_id: number | null; note: string | null; created_at: string; by_name: string | null;
}

export const membershipsApi = {
  plans: (all = false) => baseApi.get<{ enabled: boolean; plans: MembershipPlan[] }>(`/loyalty/membership-plans${all ? '?all=1' : ''}`),
  createPlan: (body: PlanInput) => baseApi.post<{ plan: MembershipPlan }>('/loyalty/membership-plans', body).then((r) => r.plan),
  updatePlan: (id: number, body: Partial<PlanInput>) => baseApi.put<{ plan: MembershipPlan }>(`/loyalty/membership-plans/${id}`, body).then((r) => r.plan),
  archivePlan: (id: number) => baseApi.del<{ ok: boolean; archived: boolean }>(`/loyalty/membership-plans/${id}`),
  list: (status: MembershipFilter = 'active') =>
    baseApi.get<{ memberships: ContactMembership[]; counts: MembershipCounts; filter: MembershipFilter }>(`/loyalty/memberships?status=${status}`),
  usage: (membershipId: number) => baseApi.get<{ usage: MembershipUsageRow[] }>(`/loyalty/memberships/${membershipId}/usage`).then((r) => r.usage),
  forContact: (contactId: string | number) => baseApi.get<ContactMembershipFicha>(`/loyalty/contacts/${contactId}/membership`),
  sell: (contactId: string | number, body: { plan_id: number; starts_on?: string | null; price_paid?: number | null; payment_note?: string | null }) =>
    baseApi.put<ContactMembershipFicha & { action: 'sale' | 'renewal' }>(`/loyalty/contacts/${contactId}/membership`, body),
  cancel: (contactId: string | number, reason?: string | null) =>
    baseApi.del<{ ok: boolean; cancelled: boolean }>(`/loyalty/contacts/${contactId}/membership${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`),
};

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
/** «19 nov» (o «19 nov 2027» si es de otro año). */
export function fechaCorta(ymd: string | null): string {
  if (!ymd) return 'sin vencimiento';
  const [y, m, d] = String(ymd).split('-').map(Number);
  if (!y || !m || !d) return ymd;
  return `${d} ${MESES[m - 1]}${y !== new Date().getFullYear() ? ` ${y}` : ''}`;
}
/** «vence el 19 nov» / «vence hoy» / «vence mañana» / «venció el 3 oct». */
export function fmtVence(m: Pick<ContactMembership, 'expires_on' | 'days_left' | 'status'>): string {
  if (m.status !== 'active') return `${m.status === 'exhausted' ? 'se agotó' : m.status === 'cancelled' ? 'cancelada' : m.status === 'replaced' ? 'cambiada' : 'venció'}${m.status === 'expired' ? ` el ${fechaCorta(m.expires_on)}` : ''}`;
  if (!m.expires_on) return 'no caduca';
  if (m.days_left === 0) return 'vence hoy';
  if (m.days_left === 1) return 'vence mañana';
  return `vence el ${fechaCorta(m.expires_on)}`;
}
/** «3 meses» · «10 productos» · «3 meses o 10 productos (lo primero)». */
export function fmtVigencia(p: Pick<MembershipPlan, 'duration_months' | 'quota_total' | 'leftover_months'>): string {
  const t = p.duration_months ? `${p.duration_months} ${p.duration_months === 1 ? 'mes' : 'meses'}` : '';
  const q = p.quota_total ? `${p.quota_total} ${p.quota_total === 1 ? 'producto incluido' : 'productos incluidos'}` : '';
  if (t && q) return `${t} o ${q} (lo primero)`;
  if (t) return t;
  if (!q) return '';
  return p.leftover_months ? `${q} (lo que sobre caduca ${p.leftover_months === 1 ? 'al mes' : `a los ${p.leftover_months} meses`})` : `${q} (no caducan)`;
}
export const STATUS_LABEL: Record<MembershipStatus, string> = {
  active: 'Vigente', expired: 'Vencida', exhausted: 'Agotada', cancelled: 'Cancelada', replaced: 'Cambiada de plan',
};
/** Texto corto de una regla de beneficio para chips (recibe el catálogo de recompensas para poner el nombre). */
export function describeBeneficio(r: BenefitRule, rewards: { id: string; title: string }[] = [], mode: 'visits' | 'cashback' = 'visits'): string {
  const nombre = (id: number) => rewards.find((x) => String(x.id) === String(id))?.title || `recompensa #${id}`;
  switch (r.type) {
    case 'checkin_reward': return `Cortesía por visita: ${nombre(r.reward_id)} (1 al día)`;
    case 'quota_reward': return r.reward_ids.length ? `Incluye: ${r.reward_ids.map(nombre).join(', ')}` : 'Incluye cualquier recompensa';
    case 'points_multiplier': return `${mode === 'cashback' ? 'Cashback' : 'Puntos'} ×${r.factor}`;
    case 'cashback_pct': return `Cashback ${r.pct}% para miembros`;
    case 'member_price': return `Precio de miembro: ${r.pct_off}% menos en caja (POS)`;
  }
}
